import { useEffect, useState } from "react";

import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminViewWithdrawHistory.css";



/* =========================================================
   COUNTRY INFORMATION
========================================================= */

const countryInfo = {
  TZS: {
    name: "Tanzania",
    flag: "🇹🇿",
  },

  KES: {
    name: "Kenya",
    flag: "🇰🇪",
  },

  UGX: {
    name: "Uganda",
    flag: "🇺🇬",
  },

  RWF: {
    name: "Rwanda",
    flag: "🇷🇼",
  },

  BIF: {
    name: "Burundi",
    flag: "🇧🇮",
  },

  CDF: {
    name: "DR Congo",
    flag: "🇨🇩",
  },

  ZMW: {
    name: "Zambia",
    flag: "🇿🇲",
  },

  MWK: {
    name: "Malawi",
    flag: "🇲🇼",
  },

  MZN: {
    name: "Mozambique",
    flag: "🇲🇿",
  },

  USD: {
    name: "Zimbabwe",
    flag: "🇿🇼",
  },

  SSP: {
    name: "South Sudan",
    flag: "🇸🇸",
  },

  BWP: {
    name: "Botswana",
    flag: "🇧🇼",
  },

  MGA: {
    name: "Madagascar",
    flag: "🇲🇬",
  },
};


/* =========================================================
   LOCAL CURRENCIES
========================================================= */

const localCurrencies = [
  "TZS",
  "KES",
  "UGX",
  "RWF",
  "BIF",
  "CDF",
  "ZMW",
  "MWK",
  "MZN",
  "USD",
  "SSP",
  "BWP",
  "MGA",
];


/* =========================================================
   CRYPTO INFORMATION
========================================================= */

const cryptoInfo = {
  BTC: {
    name: "Bitcoin",
    symbol: "BTC",
    icon: "₿",
  },

  ETH: {
    name: "Ethereum",
    symbol: "ETH",
    icon: "Ξ",
  },

  USDT: {
    name: "Tether",
    symbol: "USDT",
    icon: "₮",
  },

  USDC: {
    name: "USD Coin",
    symbol: "USDC",
    icon: "$",
  },

  TRX: {
    name: "TRON",
    symbol: "TRX",
    icon: "⚡",
  },

  LTC: {
    name: "Litecoin",
    symbol: "LTC",
    icon: "Ł",
  },

  BNB: {
    name: "BNB",
    symbol: "BNB",
    icon: "◆",
  },

  SOL: {
    name: "Solana",
    symbol: "SOL",
    icon: "◎",
  },

  XRP: {
    name: "XRP",
    symbol: "XRP",
    icon: "✕",
  },
};


/* =========================================================
   BALANCE FIELD MAP
========================================================= */

const balanceFieldMap = {
  TZS: "tshBalance",

  KES: "KESBalance",

  UGX: "UGXBalance",

  RWF: "RWFBalance",

  BIF: "BIFBalance",

  CDF: "CDFBalance",

  ZMW: "ZMWBalance",

  MWK: "MWKBalance",

  MZN: "MZNBalance",

  USD: "USDBalance",

  SSP: "SSPBalance",

  BWP: "BWPBalance",

  MGA: "MGABalance",

  USDT: "usdtBalance",

  USDC: "usdcBalance",

  BTC: "btcBalance",

  ETH: "ethBalance",

  TRX: "trxBalance",

  LTC: "ltcBalance",

  BNB: "bnbBalance",

  SOL: "solBalance",

  XRP: "xrpBalance",
};


/* =========================================================
   CRYPTO CURRENCIES
========================================================= */

const cryptoCurrencies = [
  "BTC",
  "ETH",
  "USDT",
  "USDC",
  "TRX",
  "LTC",
  "BNB",
  "SOL",
  "XRP",
];


/* =========================================================
   GET PAYOUT / RECEIVE CURRENCY
========================================================= */

const getReceiveCurrency = (item) => {
  return String(
    item?.payoutCurrency ||
      item?.receiveCurrency ||
      item?.targetCurrency ||
      item?.wallet?.currency ||
      item?.wallet?.coin ||
      item?.walletCoin ||
      item?.currency ||
      ""
  )
    .trim()
    .toUpperCase();
};


/* =========================================================
   CHECK CRYPTO WITHDRAWAL

   TYPE 1:
   USDT → TZS = PAYMENT WALLET

   TYPE 2:
   USDT → USDT = CRYPTO WALLET

   TYPE 3:
   KES → KES = PAYMENT WALLET
========================================================= */

const isCryptoWithdrawal = (item) => {
  const receiveCurrency = getReceiveCurrency(item);

  if (item?.isCrypto === true) {
    return true;
  }

  const hasCryptoAddress = Boolean(
    String(
      item?.wallet?.address ||
        item?.wallet?.walletAddress ||
        item?.walletAddress ||
        item?.address ||
        item?.cryptoAddress ||
        ""
    ).trim()
  );

  /*
    Local payout is always Payment Wallet,
    even when source is USDT.
  */

  if (localCurrencies.includes(receiveCurrency)) {
    return false;
  }

  /*
    Crypto payout currency.
  */

  if (cryptoCurrencies.includes(receiveCurrency)) {
    return true;
  }

  /*
    Crypto address.
  */

  if (hasCryptoAddress) {
    return true;
  }

  return false;
};


/* =========================================================
   FORMAT NORMAL NUMBER
========================================================= */

const formatNumber = (
  value,
  maximumFractionDigits = 2
) => {
  const number = Number(value || 0);

  return number.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }
  );
};


/* =========================================================
   FORMAT CRYPTO NUMBER
========================================================= */

const formatCryptoNumber = (
  value,
  currency
) => {
  const number = Number(value || 0);

  if (!number) {
    return "0";
  }

  const normalizedCurrency = String(
    currency || ""
  )
    .trim()
    .toUpperCase();

  if (
    normalizedCurrency === "BTC" ||
    normalizedCurrency === "ETH"
  ) {
    return number.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 8,
      }
    );
  }

  if (
    normalizedCurrency === "USDT" ||
    normalizedCurrency === "USDC"
  ) {
    return number.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      }
    );
  }

  return number.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    }
  );
};


/* =========================================================
   FORMAT DATE
========================================================= */

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  try {
    let date;

    if (
      typeof value === "object" &&
      typeof value.toDate === "function"
    ) {
      date = value.toDate();
    }

    else if (value instanceof Date) {
      date = value;
    }

    else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();

  } catch {
    return "-";
  }
};


/* =========================================================
   CREATED AT SORT VALUE
========================================================= */

const getCreatedAtValue = (value) => {
  if (!value) {
    return 0;
  }

  try {
    if (
      typeof value === "object" &&
      typeof value.toMillis === "function"
    ) {
      return value.toMillis();
    }

    if (
      typeof value === "object" &&
      typeof value.toDate === "function"
    ) {
      return value.toDate().getTime();
    }

    if (typeof value === "number") {
      return value;
    }

    const parsed = new Date(value).getTime();

    return Number.isNaN(parsed)
      ? 0
      : parsed;

  } catch {
    return 0;
  }
};


/* =========================================================
   CONVERT TO TZS

   DISPLAY ONLY
========================================================= */

const convertToTZS = (
  amount,
  currency,
  rates
) => {
  const numericAmount = Number(amount || 0);

  if (!numericAmount) {
    return 0;
  }

  const normalizedCurrency = String(
    currency || ""
  )
    .trim()
    .toUpperCase();

  if (normalizedCurrency === "TZS") {
    return numericAmount;
  }

  const rateCurrency =
    normalizedCurrency === "USDT" ||
    normalizedCurrency === "USDC"
      ? "USD"
      : normalizedCurrency;

  const currencyRate = Number(
    rates?.[rateCurrency] || 0
  );

  const tzsRate = Number(
    rates?.TZS || 0
  );

  if (!currencyRate || !tzsRate) {
    return 0;
  }

  return (
    numericAmount /
    currencyRate
  ) * tzsRate;
};


/* =========================================================
   GET USER ADDRESS

   CRYPTO ONLY
========================================================= */

const getUserAddress = (item) => {
  const possibleAddresses = [
    item?.wallet?.address,
    item?.wallet?.walletAddress,
    item?.walletAddress,
    item?.address,
    item?.userAddress,
    item?.withdrawalAddress,
    item?.cryptoAddress,
  ];

  const address =
    possibleAddresses.find(
      (value) =>
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
    );

  return address
    ? String(address).trim()
    : "";
};


/* =========================================================
   GET WALLET NETWORK
========================================================= */

const getWalletNetwork = (item) => {
  const walletSlot = String(
    item?.walletSlot ||
      item?.selectedWalletType ||
      item?.wallet?.walletSlot ||
      ""
  )
    .trim()
    .toLowerCase();

  /*
    Secondary wallet.
  */

  if (walletSlot === "secondary") {
    const secondaryNetwork =
      item?.wallet?.network2 ||
      item?.network2 ||
      item?.walletNetwork2 ||
      item?.wallet?.network ||
      item?.walletNetwork ||
      item?.network;

    if (secondaryNetwork) {
      return String(
        secondaryNetwork
      ).trim();
    }
  }

  /*
    Primary wallet.
  */

  const primaryNetwork =
    item?.wallet?.network1 ||
    item?.wallet?.network ||
    item?.walletNetwork ||
    item?.network;

  return String(
    primaryNetwork || "-"
  ).trim();
};


/* =========================================================
   GET WALLET COIN
========================================================= */

const getWalletCoin = (item) => {
  const coin =
    item?.wallet?.coin ||
    item?.walletCoin ||
    item?.targetCurrency ||
    item?.receiveCurrency ||
    item?.payoutCurrency ||
    "";

  return String(
    coin || "Crypto"
  )
    .trim()
    .toUpperCase();
};


/* =========================================================
   GET RECEIVE AMOUNT
========================================================= */

const getReceiveAmount = (item) => {
  const receiveCurrency =
    getReceiveCurrency(item);

  /*
    BTC
  */

  if (receiveCurrency === "BTC") {
    const btcAmount = Number(
      item?.actualAmountBtc || 0
    );

    if (btcAmount > 0) {
      return btcAmount;
    }
  }

  /*
    USDT
  */

  if (receiveCurrency === "USDT") {
    const usdtAmount = Number(
      item?.actualAmountUsdt || 0
    );

    if (usdtAmount > 0) {
      return usdtAmount;
    }
  }

  /*
    Other crypto.
  */

  if (
    receiveCurrency &&
    cryptoCurrencies.includes(receiveCurrency) &&
    receiveCurrency !== "BTC" &&
    receiveCurrency !== "USDT"
  ) {
    const cryptoAmountField =
      `actualAmount${receiveCurrency
        .charAt(0)
        .toUpperCase()}${receiveCurrency
        .slice(1)
        .toLowerCase()}`;

    const cryptoAmount = Number(
      item?.[cryptoAmountField] || 0
    );

    if (cryptoAmount > 0) {
      return cryptoAmount;
    }
  }

  /*
    USDT → LOCAL

    payoutAmount is already
    local currency.

    Example:
    8.5 USDT × 2660
    = 22610 TZS
  */

  const payoutAmount = Number(
    item?.payoutAmount ||
      item?.localEquivalent ||
      0
  );

  if (payoutAmount > 0) {
    return payoutAmount;
  }

  /*
    Normal local withdrawal.

    Example:
    KES 1000
    actualAmount = 950
  */

  return Number(
    item?.actualAmount || 0
  );
};


/* =========================================================
   GET SELECTED MOBILE WALLET NUMBER
========================================================= */

const getSelectedWalletNumber = (item) => {
  const walletSlot = String(
    item?.walletSlot ||
      item?.selectedWalletType ||
      item?.selectedWalletSlot ||
      item?.wallet?.walletSlot ||
      ""
  )
    .trim()
    .toLowerCase();

  /*
    SECONDARY WALLET
  */

  if (walletSlot === "secondary") {
    const secondaryNumber =
      item?.selectedWalletNumber ||
      item?.wallet?.selectedWalletNumber ||
      item?.wallet?.phoneNumber2 ||
      item?.wallet?.number2 ||
      item?.phoneNumber2 ||
      item?.number2 ||
      item?.wallet?.walletNumber ||
      item?.wallet?.number ||
      item?.wallet?.phoneNumber;

    if (secondaryNumber) {
      return String(
        secondaryNumber
      ).trim();
    }
  }

  /*
    PRIMARY WALLET
  */

  const primaryNumber =
    item?.selectedWalletNumber ||
    item?.walletNumber ||
    item?.wallet?.selectedWalletNumber ||
    item?.wallet?.walletNumber ||
    item?.wallet?.number ||
    item?.wallet?.phoneNumber ||
    item?.wallet?.phoneNumber1 ||
    item?.selectedPhoneNumber ||
    item?.phoneNumber ||
    item?.phoneNumber1;

  return String(
    primaryNumber || ""
  ).trim();
};


/* =========================================================
   GET SELECTED WALLET SLOT
========================================================= */

const getSelectedWalletSlot = (item) => {
  const slot =
    item?.walletSlot ||
    item?.selectedWalletType ||
    item?.selectedWalletSlot ||
    item?.selectedSlot ||
    item?.wallet?.walletSlot ||
    item?.wallet?.selectedWalletType ||
    "";

  return String(slot)
    .trim()
    .toLowerCase();
};


/* =========================================================
   GET REFUND AMOUNT

   ALWAYS REFUND ORIGINAL SOURCE AMOUNT
========================================================= */

const getRefundAmount = (withdrawal) => {
  return Number(
    withdrawal?.amount || 0
  );
};


/* =========================================================
   GET REFUND BALANCE FIELD

   TYPE 1:
   USDT → TZS
   = usdtBalanceOther

   TYPE 2:
   USDT → USDT
   = usdtBalance

   TYPE 3:
   KES → KES
   = KESBalance
========================================================= */

const getRefundBalanceField = (
  withdrawal,
  currency
) => {
  const possibleFields = [
    withdrawal?.balanceField,
    withdrawal?.sourceBalanceField,
    withdrawal?.balanceSource,
    withdrawal?.balanceType,
  ];

  for (const field of possibleFields) {
    const normalized = String(
      field || ""
    ).trim();

    if (!normalized) {
      continue;
    }

    if (
      Object.values(
        balanceFieldMap
      ).includes(normalized)
    ) {
      return normalized;
    }

    if (
      normalized === "usdtBalanceOther" ||
      normalized === "usdtBalance"
    ) {
      return normalized;
    }
  }

  return (
    balanceFieldMap[currency] || ""
  );
};


/* =========================================================
   GET WITHDRAWAL TYPE
========================================================= */

const getWithdrawalType = (item) => {
  const receiveCurrency =
    getReceiveCurrency(item);

  const sourceCurrency = String(
    item?.sourceCurrency ||
      item?.currency ||
      ""
  )
    .trim()
    .toUpperCase();

  /*
    TYPE 1
    USDT → LOCAL
  */

  if (
    sourceCurrency === "USDT" &&
    localCurrencies.includes(
      receiveCurrency
    )
  ) {
    return "usdt-local";
  }

  /*
    TYPE 2
    CRYPTO
  */

  if (
    isCryptoWithdrawal(item)
  ) {
    return "crypto";
  }

  /*
    TYPE 3
    LOCAL → LOCAL
  */

  return "mobile";
};


/* =========================================================
   DATE/TIME INPUT HELPER
========================================================= */

const toDateTimeLocalValue = (value) => {
  if (!value) return "";

  try {
    let date;
    if (value && typeof value.toDate === "function") {
      date = value.toDate();
    } else if (value instanceof Date) {
      date = value;
    } else if (typeof value === "number") {
      date = new Date(value);
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date.getTime())) return "";

    const pad = (number) => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  } catch {
    return "";
  }
};


/* =========================================================
   COMPONENT
========================================================= */

export default function PendingWithdrawals() {

  /* =======================================================
     DATA
  ======================================================= */

  const [
    data,
    setData,
  ] = useState([]);


  /* =======================================================
     SECURITY NAMES

     username →
     security/{username}

     Example:

     Pigiment21
       ↓
     security/Pigiment21
       ↓
     realName + lastName
  ======================================================= */

  const [
    securityNames,
    setSecurityNames,
  ] = useState({});


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     ACTION LOADING
  ======================================================= */

  const [
    actionLoading,
    setActionLoading,
  ] = useState("");


  /* =======================================================
     EXCHANGE RATES
  ======================================================= */

  const [
    rates,
    setRates,
  ] = useState({});


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     SUCCESS MESSAGE
  ======================================================= */

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");


  /* =======================================================
     EDIT WITHDRAWAL DATE / TIME
  ======================================================= */

  const [editingWithdrawal, setEditingWithdrawal] = useState(null);
  const [editedDateTime, setEditedDateTime] = useState("");
  const [savingDateTime, setSavingDateTime] = useState(false);

  /* EDIT FIRST / SECOND NAME IN security/{username} */
  const [editingNameUser, setEditingNameUser] = useState(null);
  const [editedFirstName, setEditedFirstName] = useState("");
  const [editedSecondName, setEditedSecondName] = useState("");
  const [savingName, setSavingName] = useState(false);

  /* HISTORY FILTER: Today / Yesterday / All History */
  const [historyView, setHistoryView] = useState("today");

  const getLocalDateKey = (value) => {
    if (!value) return "";
    let date;
    try {
      if (typeof value?.toDate === "function") date = value.toDate();
      else if (value instanceof Date) date = value;
      else if (typeof value === "number") date = new Date(value);
      else date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    } catch {
      return "";
    }
  };

  const todayDateKey = getLocalDateKey(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayDateKey = getLocalDateKey(yesterdayDate);

  const visibleWithdrawals = data.filter((item) => {
    const dateKey = getLocalDateKey(item.createdAt);
    if (historyView === "today") return dateKey === todayDateKey;
    if (historyView === "yesterday") return dateKey === yesterdayDateKey;
    return true;
  }).sort((a, b) => getCreatedAtValue(b.createdAt) - getCreatedAtValue(a.createdAt));



  /* =======================================================
     GET SECURITY NAME
  ======================================================= */

  const getSecurityName = async (
    username
  ) => {

    const cleanUsername = String(
      username || ""
    ).trim();

    if (!cleanUsername) {
      return {
        realName: "",
        lastName: "",
      };
    }

    try {

      /*
        IMPORTANT:

        Security collection document ID
        is the username.

        security/Pigiment21
      */

      const securityRef = doc(
        db,
        "security",
        cleanUsername
      );

      const securitySnapshot =
        await getDoc(
          securityRef
        );

      if (
        securitySnapshot.exists()
      ) {

        const securityData =
          securitySnapshot.data();

        return {
          realName: String(
            securityData?.realName || ""
          ).trim(),

          lastName: String(
            securityData?.lastName || ""
          ).trim(),
        };

      }

    } catch (securityError) {

      console.error(
        `Unable to load security/${cleanUsername}:`,
        securityError
      );

    }

    return {
      realName: "",
      lastName: "",
    };
  };


  /* =======================================================
     LOAD SECURITY NAMES

     Every withdrawal username is checked
     inside security collection.
  ======================================================= */

  const loadSecurityNames = async (
    withdrawals
  ) => {

    try {

      const usernames = [
        ...new Set(
          withdrawals
            .map(
              (item) =>
                String(
                  item?.username || ""
                ).trim()
            )
            .filter(Boolean)
        ),
      ];

      if (!usernames.length) {
        setSecurityNames({});
        return;
      }

      const entries =
        await Promise.all(
          usernames.map(
            async (username) => {

              const nameData =
                await getSecurityName(
                  username
                );

              return [
                username,
                nameData,
              ];

            }
          )
        );

      const namesMap =
        Object.fromEntries(
          entries
        );

      setSecurityNames(
        namesMap
      );

    } catch (err) {

      console.error(
        "Security names loading error:",
        err
      );

      setSecurityNames({});
    }
  };


  /* =======================================================
     GET DISPLAY NAME

     ONLY SECURITY COLLECTION
  ======================================================= */

  const getDisplayName = (
    username
  ) => {

    const cleanUsername =
      String(
        username || ""
      ).trim();

    const securityData =
      securityNames[
        cleanUsername
      ];

    if (!securityData) {
      return "No name";
    }

    const firstName =
      String(
        securityData.realName || ""
      ).trim();

    const secondName =
      String(
        securityData.lastName || ""
      ).trim();

    const fullName = [
      firstName,
      secondName,
    ]
      .filter(Boolean)
      .join(" ");

    return fullName || "No name";
  };


  /* =======================================================
     GET EXCHANGE RATES
  ======================================================= */

  const getRates = async () => {

    const cache =
      localStorage.getItem(
        "exchangeRates"
      );

    if (cache) {

      try {

        const parsed =
          JSON.parse(cache);

        const diff =
          Date.now() -
          Number(
            parsed.timestamp || 0
          );

        if (
          diff < 600000 &&
          parsed.rates
        ) {
          return parsed.rates;
        }

      } catch {
        /*
          Ignore broken cache.
        */
      }
    }

    try {

      const response =
        await fetch(
          "https://api.exchangerate-api.com/v4/latest/USD"
        );

      if (!response.ok) {
        throw new Error(
          "Unable to load exchange rates"
        );
      }

      const json =
        await response.json();

      const loadedRates =
        json?.rates || {};

      localStorage.setItem(
        "exchangeRates",
        JSON.stringify({
          rates: loadedRates,
          timestamp: Date.now(),
        })
      );

      return loadedRates;

    } catch (err) {

      console.error(
        "Exchange rate error:",
        err
      );

      return {};
    }
  };


  /* =======================================================
     FETCH WITHDRAWALS
  ======================================================= */

  const fetchWithdrawals = async () => {

    try {

      setLoading(true);

      setError("");

      setSuccessMessage("");

      /*
        Firebase MAIN DB ONLY.
      */

      const withdrawalsRef =
        collection(
          db,
          "withdrawals"
        );

      const withdrawalsQuery =
        query(
          withdrawalsRef,
          orderBy(
            "createdAt",
            "desc"
          )
        );

      const snapshot =
        await getDocs(
          withdrawalsQuery
        );

      const list =
        snapshot.docs.map(
          (withdrawalDoc) => ({
            id:
              withdrawalDoc.id,

            ...withdrawalDoc.data(),
          })
        );

      setData(list);

      /*
        IMPORTANT:

        After withdrawals load,
        load names from security collection.

        withdrawals.username
            ↓
        security/{username}
      */

      await loadSecurityNames(
        list
      );

    } catch (err) {

      console.error(
        "Withdrawal loading error:",
        err
      );

      /*
        Fallback without orderBy.
      */

      try {

        const fallbackSnapshot =
          await getDocs(
            collection(
              db,
              "withdrawals"
            )
          );

        const fallbackList =
          fallbackSnapshot.docs
            .map(
              (withdrawalDoc) => ({
                id:
                  withdrawalDoc.id,

                ...withdrawalDoc.data(),
              })
            )
            .sort(
              (a, b) =>
                getCreatedAtValue(
                  b.createdAt
                ) -
                getCreatedAtValue(
                  a.createdAt
                )
            );

        setData(
          fallbackList
        );

        /*
          Load security names
          for fallback list too.
        */

        await loadSecurityNames(
          fallbackList
        );

        setError("");

      } catch (fallbackError) {

        console.error(
          "Fallback withdrawal loading error:",
          fallbackError
        );

        setError(
          "Unable to load withdrawals."
        );

      }

    } finally {

      setLoading(false);

    }
  };


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(() => {

    let mounted = true;

    const loadData =
      async () => {

        await fetchWithdrawals();

        const loadedRates =
          await getRates();

        if (mounted) {

          setRates(
            loadedRates
          );

        }
      };

    loadData();

    return () => {
      mounted = false;
    };

  }, []);


  /* =======================================================
     STATUS COLOR
  ======================================================= */

  const getStatusClass = (
    status
  ) => {

    switch (
      String(
        status || ""
      )
        .trim()
        .toLowerCase()
    ) {

      case "processing":
        return "status-processing";

      case "reviewing":
        return "status-reviewing";

      case "reviewed":
        return "status-reviewed";

      case "success":
        return "status-success";

      case "cancelled":
        return "status-cancelled";

      case "error":
        return "status-error";

      default:
        return "status-default";
    }
  };


  /* =======================================================
     UPDATE STATUS
  ======================================================= */

  const updateStatus = async (
    id,
    newStatus
  ) => {

    if (!id || !newStatus) {
      return;
    }

    const confirmAction =
      window.confirm(
        `Change withdrawal status to "${newStatus}"?`
      );

    if (!confirmAction) {
      return;
    }

    try {

      setActionLoading(
        `${id}-${newStatus}`
      );

      setError("");

      setSuccessMessage("");

      /* =================================================
         GET WITHDRAWAL
      ================================================= */

      const withdrawalRef =
        doc(
          db,
          "withdrawals",
          id
        );

      const withdrawalSnapshot =
        await getDoc(
          withdrawalRef
        );

      if (
        !withdrawalSnapshot.exists()
      ) {

        throw new Error(
          "Withdrawal not found."
        );
      }

      const withdrawal =
        withdrawalSnapshot.data();

      const oldStatus =
        String(
          withdrawal.status || ""
        )
          .trim()
          .toLowerCase();

      /* =================================================
         ALREADY CLOSED
      ================================================= */

      if (
        oldStatus === "success" ||
        oldStatus === "cancelled"
      ) {

        alert(
          "This withdrawal is already closed."
        );

        setData(
          (previous) =>
            previous.filter(
              (item) =>
                item.id !== id
            )
        );

        return;
      }

      /* =================================================
         CANCEL / REFUND
      ================================================= */

      if (
        newStatus === "cancelled"
      ) {

        /* ===============================================
           USER INFORMATION
        =============================================== */

        const username =
          String(
            withdrawal.username || ""
          ).trim();

        const userId =
          String(
            withdrawal.userId || ""
          ).trim();

        /* ===============================================
           SOURCE CURRENCY
        =============================================== */

        const currency =
          String(
            withdrawal.currency ||
              withdrawal.sourceCurrency ||
              "TZS"
          )
            .trim()
            .toUpperCase();

        /* ===============================================
           ORIGINAL AMOUNT
        =============================================== */

        const refundAmount =
          getRefundAmount(
            withdrawal
          );

        if (
          !username &&
          !userId
        ) {

          throw new Error(
            "Withdrawal user information is missing."
          );
        }

        if (
          !refundAmount ||
          refundAmount <= 0
        ) {

          throw new Error(
            "Invalid withdrawal amount."
          );
        }

        /* ===============================================
           BALANCE FIELD
        =============================================== */

        const finalBalanceField =
          getRefundBalanceField(
            withdrawal,
            currency
          );

        if (!finalBalanceField) {

          throw new Error(
            `Unsupported balance field for currency: ${currency}`
          );
        }

        /* ===============================================
           FIND USER
        =============================================== */

        let userDoc = null;

        /*
          FIRST:
          Find directly using userId.
        */

        if (userId) {

          const directUserRef =
            doc(
              db,
              "users",
              userId
            );

          const directUserSnapshot =
            await getDoc(
              directUserRef
            );

          if (
            directUserSnapshot.exists()
          ) {

            userDoc =
              directUserSnapshot;
          }
        }

        /*
          SECOND:
          Fallback to username.
        */

        if (
          !userDoc &&
          username
        ) {

          const userQuery =
            query(
              collection(
                db,
                "users"
              ),

              where(
                "username",
                "==",
                username
              )
            );

          const userSnapshot =
            await getDocs(
              userQuery
            );

          if (
            !userSnapshot.empty
          ) {

            userDoc =
              userSnapshot.docs[0];
          }
        }

        if (!userDoc) {

          throw new Error(
            "User account was not found."
          );
        }

        /* ===============================================
           USER BALANCE
        =============================================== */

        const userData =
          userDoc.data();

        const currentBalance =
          Number(
            userData[
              finalBalanceField
            ] || 0
          );

        const newBalance =
          currentBalance +
          refundAmount;

        /* ===============================================
           UPDATE USER BALANCE
        =============================================== */

        await updateDoc(
          userDoc.ref,
          {
            [finalBalanceField]:
              newBalance,
          }
        );
      }

      /* =================================================
         UPDATE WITHDRAWAL STATUS
      ================================================= */

      await updateDoc(
        withdrawalRef,
        {
          status:
            newStatus,

          updatedAt:
            new Date(),
        }
      );

      /* =================================================
         REMOVE CLOSED WITHDRAWALS
      ================================================= */

      if (
        newStatus === "success" ||
        newStatus === "cancelled"
      ) {

        setData(
          (previous) =>
            previous.filter(
              (item) =>
                item.id !== id
            )
        );

      } else {

        setData(
          (previous) =>
            previous.map(
              (item) =>
                item.id === id
                  ? {
                      ...item,
                      status:
                        newStatus,
                    }
                  : item
            )
        );
      }

      /* =================================================
         SUCCESS MESSAGE
      ================================================= */

      if (
        newStatus === "cancelled"
      ) {

        const successCurrency =
          String(
            withdrawal.currency ||
              withdrawal.sourceCurrency ||
              "TZS"
          )
            .trim()
            .toUpperCase();

        const successRefundAmount =
          getRefundAmount(
            withdrawal
          );

        const successBalanceField =
          getRefundBalanceField(
            withdrawal,
            successCurrency
          );

        setSuccessMessage(
          `Withdrawal cancelled and ${formatNumber(
            successRefundAmount
          )} ${successCurrency} refunded to ${successBalanceField}.`
        );

      } else {

        setSuccessMessage(
          `Withdrawal status changed to "${newStatus}".`
        );
      }

    } catch (err) {

      console.error(
        "Status update error:",
        err
      );

      setError(
        err?.message ||
          "Unable to update withdrawal."
      );

      alert(
        err?.message ||
          "Unable to update withdrawal."
      );

    } finally {

      setActionLoading("");

    }
  };


  /* =======================================================
     OPEN / SAVE FIRST AND SECOND NAME
     security/{username}: realName = first name, lastName = second name
  ======================================================= */

  const openNameEditor = (username) => {
    const cleanUsername = String(username || "").trim();
    const nameData = securityNames[cleanUsername] || {};
    setEditingNameUser(cleanUsername);
    setEditedFirstName(String(nameData.realName || ""));
    setEditedSecondName(String(nameData.lastName || ""));
    setError("");
    setSuccessMessage("");
  };

  const saveSecurityName = async () => {
    const username = String(editingNameUser || "").trim();

    if (!username) {
      setError("Username haipo. Imeshindikana kuhifadhi majina.");
      return;
    }

    try {
      setSavingName(true);
      setError("");
      setSuccessMessage("");

      const securityRef = doc(db, "security", username);
      const securitySnapshot = await getDoc(securityRef);

      if (!securitySnapshot.exists()) {
        throw new Error(`Document security/${username} haipo.`);
      }

      await updateDoc(securityRef, {
        realName: editedFirstName.trim(),
        lastName: editedSecondName.trim(),
      });

      setSecurityNames((previous) => ({
        ...previous,
        [username]: {
          ...(previous[username] || {}),
          realName: editedFirstName.trim(),
          lastName: editedSecondName.trim(),
        },
      }));

      setSuccessMessage(`Majina ya ${username} yamehifadhiwa.`);
      setEditingNameUser(null);
      setEditedFirstName("");
      setEditedSecondName("");
    } catch (err) {
      console.error("Security name update error:", err);
      setError(err?.message || "Imeshindikana kuhifadhi majina.");
    } finally {
      setSavingName(false);
    }
  };


  /* =======================================================
     DELETE WITHDRAWAL TRANSACTION
  ======================================================= */

  const deleteWithdrawal = async (item) => {
    if (!item?.id) {
      setError("Imeshindikana kupata ID ya muamala.");
      return;
    }

    const confirmed = window.confirm(
      `Una uhakika unataka kufuta muamala huu wa ${item.username || "mtumiaji"}? Hatua hii haiwezi kurudishwa.`
    );
    if (!confirmed) return;

    try {
      setActionLoading(item.id);
      setError("");
      setSuccessMessage("");
      await deleteDoc(doc(db, "withdrawals", item.id));
      setData((previous) => previous.filter((record) => record.id !== item.id));
      setSuccessMessage("Muamala umefutwa kikamilifu.");
    } catch (err) {
      console.error("Withdrawal delete error:", err);
      setError(err?.message || "Imeshindikana kufuta muamala.");
    } finally {
      setActionLoading("");
    }
  };


  /* =======================================================
     OPEN DATE / TIME EDITOR
  ======================================================= */

  const openDateTimeEditor = (item) => {
    setEditingWithdrawal(item);
    setEditedDateTime(toDateTimeLocalValue(item.createdAt));
    setError("");
    setSuccessMessage("");
  };

  /* =======================================================
     SAVE DATE / TIME

     Updates createdAt, the date/time shown in the record.
  ======================================================= */

  const saveWithdrawalDateTime = async () => {
    if (!editingWithdrawal?.id || !editedDateTime) {
      setError("Tafadhali chagua tarehe na muda sahihi.");
      return;
    }

    const newDate = new Date(editedDateTime);
    if (Number.isNaN(newDate.getTime())) {
      setError("Tarehe au muda si sahihi.");
      return;
    }

    try {
      setSavingDateTime(true);
      setError("");
      setSuccessMessage("");

      const withdrawalRef = doc(db, "withdrawals", editingWithdrawal.id);
      await updateDoc(withdrawalRef, {
        createdAt: newDate,
        updatedAt: new Date(),
      });

      setData((previous) =>
        previous
          .map((item) => item.id === editingWithdrawal.id
            ? { ...item, createdAt: newDate, updatedAt: new Date() }
            : item)
          .sort((a, b) => getCreatedAtValue(b.createdAt) - getCreatedAtValue(a.createdAt))
      );

      setSuccessMessage("Tarehe, saa, dakika na sekunde za withdrawal vimehifadhiwa.");
      setEditingWithdrawal(null);
      setEditedDateTime("");
    } catch (err) {
      console.error("Date/time update error:", err);
      setError(err?.message || "Imeshindikana kuhifadhi tarehe na muda.");
    } finally {
      setSavingDateTime(false);
    }
  };


  /* =======================================================
     ACTIVE WITHDRAWALS
  ======================================================= */

  const activeWithdrawals =
    data.filter(
      (item) => {

        const status =
          String(
            item.status || ""
          )
            .trim()
            .toLowerCase();

        return (
          status !== "success" &&
          status !== "cancelled"
        );
      }
    );


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <div className="pending-withdrawals-page">

        <div className="pending-withdrawals-loading">

          <div className="pending-withdrawals-spinner" />

          <span>
            Loading withdrawals...
          </span>

        </div>

      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="pending-withdrawals-page">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="pending-withdrawals-header">

        <div>

          <h2>
            Withdraw Management
          </h2>

          <span>
            Withdrawal records by date
          </span>

        </div>

        <button
          type="button"
          className="pending-withdrawals-refresh"
          onClick={fetchWithdrawals}
          disabled={loading}
        >
          Refresh
        </button>

      </div>


      <div className="withdraw-history-tabs" role="tablist" aria-label="Withdrawal history date filter">
        <button type="button" className={historyView === "today" ? "active" : ""} onClick={() => setHistoryView("today")}>
          Today
          <span>{data.filter((item) => getLocalDateKey(item.createdAt) === todayDateKey).length}</span>
        </button>
        <button type="button" className={historyView === "yesterday" ? "active" : ""} onClick={() => setHistoryView("yesterday")}>
          Yesterday
          <span>{data.filter((item) => getLocalDateKey(item.createdAt) === yesterdayDateKey).length}</span>
        </button>
        <button type="button" className={historyView === "history" ? "active" : ""} onClick={() => setHistoryView("history")}>
          All History
          <span>{data.length}</span>
        </button>
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (

        <div className="pending-withdrawals-alert error">

          <span>
            ⚠️
          </span>

          <span>
            {error}
          </span>

        </div>
      )}


      {/* =====================================================
          SUCCESS
      ===================================================== */}

      {successMessage && (

        <div className="pending-withdrawals-alert success">

          <span>
            ✓
          </span>

          <span>
            {successMessage}
          </span>

        </div>
      )}


      {/* =====================================================
          COUNT
      ===================================================== */}

      <div className="pending-withdrawals-count">

        <span>
          Withdrawal Records
        </span>

        <b>
          {visibleWithdrawals.length}
        </b>

      </div>


      {/* =====================================================
          EMPTY
      ===================================================== */}

      {visibleWithdrawals.length === 0 ? (

        <div className="pending-withdrawals-empty">

          <div className="empty-icon">
            ✓
          </div>

          <strong>
            No records found
          </strong>

          <span>
            No withdrawals found for this date/filter.
          </span>

        </div>

      ) : (

        <div className="pending-withdrawals-list">

          {visibleWithdrawals.map(
            (item) => {

              /* =============================================
                 BASIC DATA
              ============================================= */

              const status =
                String(
                  item.status ||
                    "processing"
                )
                  .trim()
                  .toLowerCase();


              /* =============================================
                 USERNAME
              ============================================= */

              const username =
                String(
                  item.username ||
                    ""
                ).trim();


              /*
                IMPORTANT:

                Name comes ONLY from:

                security/{username}

                NOT from withdrawals.realName
                NOT from withdrawals.lastName
              */

              const displayName =
                getDisplayName(
                  username
                );


              /* =============================================
                 SOURCE CURRENCY
              ============================================= */

              const currency =
                String(
                  item.currency ||
                    item.sourceCurrency ||
                    "TZS"
                )
                  .trim()
                  .toUpperCase();


              /* =============================================
                 RECEIVE / PAYOUT CURRENCY
              ============================================= */

              const receiveCurrency =
                getReceiveCurrency(
                  item
                );


              /* =============================================
                 EXACT WITHDRAWAL TYPE
              ============================================= */

              const withdrawalType =
                getWithdrawalType(
                  item
                );


              /*
                TYPE 1:
                USDT → TZS
                crypto = false

                TYPE 2:
                USDT → USDT
                crypto = true

                TYPE 3:
                KES → KES
                crypto = false
              */

              const crypto =
                withdrawalType ===
                "crypto";


              const walletCoin =
                getWalletCoin(
                  item
                );


              const walletNetwork =
                getWalletNetwork(
                  item
                );


              const userAddress =
                getUserAddress(
                  item
                );


              const selectedWalletNumber =
                getSelectedWalletNumber(
                  item
                );


              const selectedWalletSlot =
                getSelectedWalletSlot(
                  item
                );


              /* =============================================
                 AMOUNTS
              ============================================= */

              const amount =
                Number(
                  item.amount || 0
                );


              const actualAmount =
                Number(
                  item.actualAmount || 0
                );


              const receiveAmount =
                getReceiveAmount(
                  item
                );


              const feeAmount =
                Number(
                  item.feeAmount || 0
                );


              /* =============================================
                 TZS VALUE
              ============================================= */

              let tzsValue = 0;

              if (
                receiveCurrency === "TZS"
              ) {

                /*
                  USDT → TZS

                  payoutAmount is already TZS.
                */

                tzsValue =
                  Number(
                    receiveAmount || 0
                  );

              } else {

                const tzsSourceAmount =
                  actualAmount ||
                  amount;

                tzsValue =
                  convertToTZS(
                    tzsSourceAmount,
                    currency,
                    rates
                  );
              }


              /* =============================================
                 COUNTRY
              ============================================= */

              const displayCurrency =
                localCurrencies.includes(
                  receiveCurrency
                )
                  ? receiveCurrency
                  : currency;


              const country =
                countryInfo[
                  displayCurrency
                ] || {

                  name:
                    item.country ||
                    "International",

                  flag:
                    "🌍",
                };


              /* =============================================
                 CRYPTO INFO
              ============================================= */

              const cryptoData =
                cryptoInfo[
                  walletCoin
                ] || {

                  name:
                    walletCoin,

                  symbol:
                    walletCoin,

                  icon:
                    "₿",
                };


              return (

                <div
                  key={item.id}
                  className={
                    `pending-withdrawal-card ${
                      crypto
                        ? "crypto-withdrawal-card"
                        : ""
                    } ${
                      withdrawalType ===
                      "usdt-local"
                        ? "usdt-local-withdrawal-card"
                        : ""
                    }`
                  }
                >

                  <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 12px 0" }}>
                    <button
                      type="button"
                      onClick={() => deleteWithdrawal(item)}
                      disabled={actionLoading === item.id}
                      style={{
                        background: "#dc2626",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "7px 12px",
                        cursor: actionLoading === item.id ? "not-allowed" : "pointer",
                        opacity: actionLoading === item.id ? 0.65 : 1,
                        fontWeight: 600,
                      }}
                    >
                      {actionLoading === item.id ? "Inafuta..." : "Futa Muamala"}
                    </button>
                  </div>

                  {/* =========================================
                      CARD HEADER
                  ========================================= */}

                  <div className="pending-card-header">

                    <div className="pending-user">

                      <div className="pending-user-avatar">

                        {String(
                          username || "U"
                        )
                          .charAt(0)
                          .toUpperCase()}

                      </div>


                      <div className="pending-user-info">

                        <strong>
                          {username ||
                            "Unknown User"}
                        </strong>


                        {/* =================================
                            NAME FROM SECURITY ONLY
                        ================================= */}

                        <span>
                          {displayName}
                        </span>

                        <button
                          type="button"
                          className="pending-edit-name-button"
                          onClick={() => openNameEditor(username)}
                          disabled={!username || Boolean(actionLoading) || savingName}
                        >
                          Edit First / Second Name
                        </button>

                      </div>

                    </div>


                    <div className={`pending-status ${getStatusClass(status)}`} >

                      {item.status ||
                        "processing"}

                    </div>

                  </div>



{/*
  =========================================
  CURRENCY / COUNTRY
  =========================================
*/}


             {crypto ? (

                    <div className="pending-country crypto-country">

                      <span className="pending-country-flag">

                        {cryptoData.icon}

                      </span>


                      <span>
                        {cryptoData.name}
                      </span>


                      <b>
                        {walletCoin}
                      </b>

                    </div>

                  ) : (

                    <div className="pending-country">

                      <span className="pending-country-flag">

                        {country.flag}

                      </span>


                      <span>
                        {country.name}
                      </span>


                      <b>
                        {displayCurrency}
                      </b>

                    </div>

                  )}


                  {/* =========================================
                      AMOUNT
                  ========================================= */}

                  <div className="pending-amount-box">

                    {/* ---------------------------------------
                        WITHDRAWAL AMOUNT
                    --------------------------------------- */}

                    <div>

                      <span>
                        Withdrawal Amount
                      </span>


                      <strong>

                        {currency}{" "}

                        {crypto
                          ? formatCryptoNumber(
                              amount,
                              currency
                            )
                          : formatNumber(
                              amount
                            )}

                      </strong>

                    </div>


                    {/* ---------------------------------------
                        USER RECEIVES
                    --------------------------------------- */}

                    <div>

                      <span>
                        User Receives
                      </span>


                      <strong className="receive">

                        {receiveCurrency ||
                          currency}{" "}

                        {crypto
                          ? formatCryptoNumber(
                              receiveAmount,
                              receiveCurrency
                            )
                          : formatNumber(
                              receiveAmount
                            )}

                      </strong>

                    </div>


                    {/* ---------------------------------------
                        FEE
                    --------------------------------------- */}

                    {feeAmount > 0 && (

                      <div>

                        <span>
                          Fee
                        </span>


                        <strong>

                          {currency}{" "}

                          {crypto
                            ? formatCryptoNumber(
                                feeAmount,
                                currency
                              )
                            : formatNumber(
                                feeAmount
                              )}

                        </strong>

                      </div>
                    )}


                    {/* ---------------------------------------
                        TZS VALUE
                    --------------------------------------- */}

                    {tzsValue > 0 && (

                      <div className="tzs-value">

                        ≈ TZS{" "}

                        {formatNumber(
                          tzsValue
                        )}

                      </div>
                    )}

                  </div>


                  {/* =========================================
                      WALLET
                  ========================================= */}

                  {crypto ? (

                    <div className="pending-wallet-box crypto-wallet-box">

                      <div className="pending-wallet-icon">
                        {cryptoData.icon}
                      </div>


                      <div className="pending-wallet-info">

                        <span>
                          Crypto Wallet
                        </span>


                        <strong>

                          {walletCoin}
                          {" — "}
                          {walletNetwork}

                        </strong>


                        <b>

                          {userAddress ||
                            "No address provided"}

                        </b>

                      </div>

                    </div>

                  ) : (

                    <div className="pending-wallet-box">

                      <div className="pending-wallet-icon">
                        📱
                      </div>


                      <div className="pending-wallet-info">

                        <span>
                          Payment Wallet
                        </span>


                        <strong>

                          {walletNetwork ||
                            "Mobile Wallet"}

                        </strong>


                        <b>

                          {item.walletCode ||
                            item.wallet?.code ||
                            ""}

                          {selectedWalletNumber ||
                            "No number"}

                        </b>


                        {selectedWalletSlot && (

                          <small>

                            Wallet:{" "}

                            {selectedWalletSlot}

                          </small>

                        )}

                      </div>

                    </div>

                  )}


                  {/* =========================================
                      WITHDRAWAL DETAILS
                  ========================================= */}

                  <div className="pending-details">

                    <div>

                      <span>
                        Withdrawal Type
                      </span>


                      <b>

                        {item.withdrawalType ||

                          (
                            withdrawalType ===
                            "usdt-local"
                              ? "USDT → Local Wallet"
                              : withdrawalType ===
                                "crypto"
                              ? "Crypto"
                              : "Local Wallet"
                          )}

                      </b>

                    </div>


                    <div>

                      <span>
                        Processing Time
                      </span>


                      <b>
                        {item.processingTime ||
                          "-"}
                      </b>

                    </div>


                    <div>

                      <span>
                        Fee
                      </span>


                      <b>

                        {item.fee !==
                        undefined

                          ? `${item.fee}%`

                          : "-"}

                      </b>

                    </div>


                    <div>

                      <span>
                        Fee Amount
                      </span>


                      <b>

                        {currency}{" "}

                        {crypto
                          ? formatCryptoNumber(
                              feeAmount,
                              currency
                            )
                          : formatNumber(
                              feeAmount
                            )}

                      </b>

                    </div>


                    {/* =======================================
                        BALANCE SOURCE
                    ======================================= */}

                    {(item.balanceField ||
                      item.sourceBalanceField ||
                      item.balanceSource ||
                      item.balanceType) && (

                      <div>

                        <span>
                          Balance Source
                        </span>


                        <b>

                          {item.balanceField ||

                            item.sourceBalanceField ||

                            item.balanceSource ||

                            item.balanceType}

                        </b>

                      </div>

                    )}


                    {/* =======================================
                        CRYPTO CONVERSION
                    ======================================= */}

                    {crypto &&
                      item.conversionRate && (

                        <div>

                          <span>
                            Conversion Rate
                          </span>


                          <b>

                            {formatCryptoNumber(
                              item.conversionRate,
                              receiveCurrency
                            )}

                          </b>

                        </div>
                      )}


                    {/* =======================================
                        SOURCE CURRENCY
                    ======================================= */}

                    {crypto &&
                      item.sourceCurrency && (

                        <div>

                          <span>
                            Source Currency
                          </span>


                          <b>

                            {String(
                              item.sourceCurrency
                            )
                              .toUpperCase()}

                          </b>

                        </div>
                      )}


                    {/* =======================================
                        TARGET CURRENCY
                    ======================================= */}

                    {crypto &&
                      item.targetCurrency && (

                        <div>

                          <span>
                            Target Currency
                          </span>


                          <b>

                            {String(
                              item.targetCurrency
                            )
                              .toUpperCase()}

                          </b>

                        </div>
                      )}


                    {/* =======================================
                        PAYOUT CURRENCY
                    ======================================= */}

                    {!crypto &&
                      currency === "USDT" &&
                      receiveCurrency && (

                        <div>

                          <span>
                            Payout Currency
                          </span>


                          <b>
                            {receiveCurrency}
                          </b>

                        </div>
                      )}

                  </div>


                  {/* =========================================
                      DATE
                  ========================================= */}

                  <div className="pending-date">

                    <span>
                      Created
                    </span>


                    <b>

                      {formatDate(
                        item.createdAt
                      )}

                    </b>

                  </div>


                  {/* =========================================
                      STATUS CONTROL
                  ========================================= */}

                  <div className="pending-action">

                    <button
                      type="button"
                      className="pending-edit-datetime-button"
                      onClick={() => openDateTimeEditor(item)}
                      disabled={Boolean(actionLoading) || savingDateTime}
                    >
                      Edit Date / Time
                    </button>

                    {status !== "success" && status !== "cancelled" && (
                    <select
                      className="pending-status-select"
                      defaultValue=""
                      disabled={
                        actionLoading.startsWith(
                          `${item.id}-`
                        )
                      }
                      onChange={(event) => {

                        const newStatus =
                          event.target.value;

                        if (newStatus) {

                          updateStatus(
                            item.id,
                            newStatus
                          );
                        }

                      }}
                    >

                      <option
                        value=""
                        disabled
                      >
                        Change status
                      </option>


                      <option value="reviewing">
                        Reviewing
                      </option>


                      <option value="reviewed">
                        Reviewed
                      </option>


                      <option value="success">
                        Success
                      </option>


                      <option value="cancelled">
                        Cancel
                      </option>

                    </select>
                    )}


                    {actionLoading &&
                      actionLoading.startsWith(
                        `${item.id}-`
                      ) && (

                        <div className="pending-action-loading">

                          <span className="pending-mini-spinner" />

                          Updating...

                        </div>
                      )}

                  </div>

                </div>
              );
            }
          )}

        </div>
      )}

      {editingNameUser && (
        <div
          className="pending-datetime-modal-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !savingName) {
              setEditingNameUser(null);
            }
          }}
        >
          <div
            className="pending-datetime-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pending-name-title"
          >
            <div className="pending-datetime-modal-header">
              <div>
                <span>ADMIN CONTROL</span>
                <h3 id="pending-name-title">Edit first and second name</h3>
                <p>User: {editingNameUser}</p>
              </div>
              <button
                type="button"
                className="pending-datetime-close"
                onClick={() => setEditingNameUser(null)}
                disabled={savingName}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <label className="pending-datetime-label" htmlFor="security-first-name">
              First Name (realName)
            </label>
            <input
              id="security-first-name"
              className="pending-datetime-input"
              type="text"
              value={editedFirstName}
              onChange={(event) => setEditedFirstName(event.target.value)}
              disabled={savingName}
              autoComplete="given-name"
            />

            <label className="pending-datetime-label" htmlFor="security-second-name">
              Second Name (lastName)
            </label>
            <input
              id="security-second-name"
              className="pending-datetime-input"
              type="text"
              value={editedSecondName}
              onChange={(event) => setEditedSecondName(event.target.value)}
              disabled={savingName}
              autoComplete="family-name"
            />

            <p className="pending-datetime-note">
              Majina haya yatahifadhiwa kwenye security/{editingNameUser}. First Name hutumia realName na Second Name hutumia lastName.
            </p>

            <div className="pending-datetime-actions">
              <button
                type="button"
                className="pending-datetime-cancel"
                onClick={() => setEditingNameUser(null)}
                disabled={savingName}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pending-datetime-save"
                onClick={saveSecurityName}
                disabled={savingName}
              >
                {savingName ? "Saving..." : "Save Names"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingWithdrawal && (
        <div
          className="pending-datetime-modal-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget && !savingDateTime) {
              setEditingWithdrawal(null);
            }
          }}
        >
          <div
            className="pending-datetime-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pending-datetime-title"
          >
            <div className="pending-datetime-modal-header">
              <div>
                <span>ADMIN CONTROL</span>
                <h3 id="pending-datetime-title">Edit withdrawal date &amp; time</h3>
                <p>User: {editingWithdrawal.username || "Unknown"}</p>
              </div>
              <button
                type="button"
                className="pending-datetime-close"
                onClick={() => setEditingWithdrawal(null)}
                disabled={savingDateTime}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <label className="pending-datetime-label" htmlFor="withdrawal-created-at">
              Date and time (including seconds)
            </label>
            <input
              id="withdrawal-created-at"
              className="pending-datetime-input"
              type="datetime-local"
              step="1"
              value={editedDateTime}
              onChange={(event) => setEditedDateTime(event.target.value)}
              disabled={savingDateTime}
              required
            />

            <p className="pending-datetime-note">
              Hii itabadilisha tarehe na muda unaoonekana kwenye rekodi (Created) na mpangilio wa rekodi.
            </p>

            <div className="pending-datetime-actions">
              <button
                type="button"
                className="pending-datetime-cancel"
                onClick={() => setEditingWithdrawal(null)}
                disabled={savingDateTime}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pending-datetime-save"
                onClick={saveWithdrawalDateTime}
                disabled={savingDateTime || !editedDateTime}
              >
                {savingDateTime ? "Saving..." : "Save date & time"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
        }






